import { useSignInWithEmailPass } from "@/queries/auth/auth";
import { profileQuery } from "@/queries/customer/customer";
import { queryClient } from "@/queries/queryClient";
import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Button, Heading, Hint, Input, Text } from "@medusajs/ui";
import { Form } from "@repo/design-system";
import {
  createRoute,
  Link,
  useNavigate,
  useSearch
} from "@tanstack/react-router";
import { rootRoute } from "../$rootRoute";
import type { LoginFormDataType } from "./validations";
import { LoginSchema } from "./validations";

import { AvatarBox } from "@/components/logo";
import { analytics, beginSelfServeSession } from "@/lib/analytics";
import { handleActionError } from "@/lib/errors";
import { safeRedirectTarget } from "@/lib/navigation/safe-redirect";
import { buildSupportWhatsAppUrl } from "@/lib/support";
import { isFetchError } from "@repo/api-sdk";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";

export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "login",
  validateSearch: (search?: Record<string, unknown>): { redirect?: string } => {
    return {
      redirect: (search?.redirect as string) || undefined
    };
  },
  component: LoginPage
});

function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { redirect } = useSearch({ from: loginRoute.id });
  const { mutateAsync, isPending } = useSignInWithEmailPass();

  const form = useForm<LoginFormDataType>({
    resolver: zodResolver(LoginSchema),
    defaultValues: {
      email: "",
      password: ""
    }
  });

  const handleSubmit = form.handleSubmit(async ({ email, password }) => {
    await mutateAsync(
      {
        email,
        password
      },
      {
        onError: (error) => {
          if (isFetchError(error)) {
            if (error.status === 401) {
              form.setError("email", {
                type: "manual",
                message: error.message
              });

              return;
            }
          }

          form.setError("root.serverError", {
            type: "manual",
            message: handleActionError(error, {
              boundary: "login",
              fallback: "We couldn't sign you in. Please try again.",
              trustClientErrors: true
            })
          });
        },
        onSuccess: async () => {
          // A password login is self-serve by definition, so any actor a
          // concierge redemption left behind must go.
          beginSelfServeSession();

          await queryClient.invalidateQueries({
            queryKey: profileQuery().queryKey
          });
          // `redirect` is a search param anyone can craft a link with, so it
          // is resolved to an in-app path before it is followed.
          navigate({ href: safeRedirectTarget(redirect), replace: true });
        }
      }
    );
  });

  const serverError = form.formState.errors.root?.serverError?.message;
  const validationError =
    form.formState.errors.email?.message ||
    form.formState.errors.password?.message;

  return (
    <div className="bg-ui-bg-subtle flex min-h-dvh w-dvw items-center justify-center">
      <div className="m-4 flex w-full max-w-[280px] flex-col items-center">
        <AvatarBox checked={false} />
        <div className="mb-4 flex flex-col items-center">
          <Heading>{t("login.title")}</Heading>
          <Text size="small" className="text-ui-fg-subtle text-center">
            {t("login.hint")}
          </Text>
        </div>
        <div className="flex w-full flex-col gap-y-3">
          <Form {...form}>
            <form
              onSubmit={handleSubmit}
              className="flex w-full flex-col gap-y-6"
            >
              <div className="flex flex-col gap-y-1">
                <Form.Field
                  control={form.control}
                  name="email"
                  render={({ field }) => {
                    return (
                      <Form.Item>
                        <Form.Control>
                          <Input
                            autoComplete="email"
                            {...field}
                            className="bg-ui-bg-field-component"
                            placeholder={t("fields.email")}
                          />
                        </Form.Control>
                      </Form.Item>
                    );
                  }}
                />
                <Form.Field
                  control={form.control}
                  name="password"
                  render={({ field }) => {
                    return (
                      <Form.Item>
                        <Form.Label>{}</Form.Label>
                        <Form.Control>
                          <Input
                            type="password"
                            autoComplete="current-password"
                            {...field}
                            className="bg-ui-bg-field-component"
                            placeholder={t("fields.password")}
                          />
                        </Form.Control>
                      </Form.Item>
                    );
                  }}
                />
              </div>
              {validationError && (
                <div className="text-center">
                  <Hint className="inline-flex" variant={"error"}>
                    {validationError}
                  </Hint>
                </div>
              )}
              {serverError && (
                <Alert
                  className="bg-ui-bg-base items-center p-2"
                  dismissible
                  variant="error"
                >
                  {serverError}
                </Alert>
              )}
              <Button className="w-full" type="submit" isLoading={isPending}>
                {t("actions.continueWithEmail")}
              </Button>
            </form>
          </Form>
        </div>
        <span className="text-ui-fg-muted txt-small my-6">
          <Trans
            i18nKey="login.forgotPassword"
            components={[
              <Link
                key="forgotPassword"
                to="/reset-password"
                className="text-ui-fg-interactive transition-fg hover:text-ui-fg-interactive-hover focus-visible:text-ui-fg-interactive-hover font-medium outline-none"
              />
            ]}
          />
        </span>
        <a
          href={buildSupportWhatsAppUrl(null, "/login")}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => analytics.supportWhatsAppOpened({ page: "login" })}
          className="text-ui-fg-muted txt-small hover:text-ui-fg-subtle"
        >
          {t("login.needHelp")}
        </a>
      </div>
    </div>
  );
}
